"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge } from "@/components/ui/Badge";
import type { ReactNode } from "react";
import { useId } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { TooltipContentProps } from "recharts";
import cn from "twsx";

// One chart for every data view (assistant answers, Diffuse reports): token
// colours, Linear's 11px tertiary axis ticks, a dashed grid, the popover
// surface for tooltips, and no animation. Callers pass points and pick a
// kind; they never reach into recharts.

/** Categorical series colours, in the order a chart assigns them. */
export const chartPalette = ["var(--color-primary-600)", "var(--chart-categorical-7)", "var(--color-navy-700)", "var(--color-primary-800)", "var(--color-navy-500)", "var(--chart-categorical-5)", "var(--color-navy-300)", "var(--color-gray-400)"] as const;

/** The single-series colour, the colour of a collapsed "others" bucket, and the muted colour for de-emphasised bars. */
export const chartColors = {
  series: "var(--color-primary-600)",
  other: "var(--chart-categorical-6)",
  muted: "var(--color-navy-200)"
} as const;

/** Named colours a model may request, mapped onto the palette. */
const namedColors: Record<string, string> = {
  blue: chartPalette[0],
  sky: chartPalette[1],
  navy: chartPalette[2],
  slate: chartPalette[4],
  gray: chartPalette[7]
};

/** The palette colour for a series index, or for a requested colour name when it is one the palette knows. */
export function chartColor(index: number, name?: string): string {
  const requested = name?.trim().toLowerCase();
  if (requested && requested in namedColors) return namedColors[requested]!;
  return chartPalette[index % chartPalette.length]!;
}
export type ChartPoint = {
  label: string;
  value: number;
  color?: string;
};
const fullNumber = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2
});
const compactNumber = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1
});
const shortNumber = (value: number) => Math.abs(value) < 10_000 ? fullNumber.format(value) : compactNumber.format(value);
const tick = {
  fill: "var(--color-text-tertiary)",
  fontSize: 11
};
const categoryTick = {
  fill: "var(--color-text-secondary)",
  fontSize: 12
};
const valueLabelClassName = "fill-text-secondary text-xs font-medium tabular-nums";
const still = {
  isAnimationActive: false
} as const;
const categoryLabel = (name: string) => name.length > 14 ? `${name.slice(0, 13)}…` : name;
const plotHeights = {
  sm: 196,
  md: 220,
  lg: 300
} as const;
const barRowHeight = 30;
const plotClassName = cn("w-full min-w-0 [&_.recharts-cartesian-axis-tick_text]:tabular-nums", "[&_.recharts-surface:focus]:outline-none [&_.recharts-wrapper:focus]:outline-none");

/** A colour chip for legends, tooltips and table rows that match chart series. */
export function ChartSwatch(t0) {
  const $ = _c(2);
  const {
    color
  } = t0;
  const t1 = color ?? "transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = <span aria-hidden="true" className="inline-block size-2 shrink-0 rounded-xs" style={{
      background: t1
    }} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}

/** The headline above a chart: a large figure, what it counts, and an optional change badge. */
export function ChartSummary(t0) {
  const $ = _c(11);
  const {
    value,
    label,
    change,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[0] !== size || $[1] !== value) {
    t2 = value && <strong className={cn("font-semibold leading-tight tracking-tight tabular-nums text-text-primary", size === "sm" ? "text-xl" : "text-2xl")}>{value}</strong>;
    $[0] = size;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <span>{label}</span>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== change) {
    t4 = change && <Badge color={change.direction === "up" ? "success" : change.direction === "down" ? "danger" : "neutral"}>{change.text}</Badge>;
    $[5] = change;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-xs text-text-tertiary">{t2}{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
type TooltipProps = TooltipContentProps<number | string | ReadonlyArray<number | string>, number | string> & {
  valueLabel: string;
  detail?: (value: number) => {
    label: string;
    value: string;
  } | undefined;
};
function ChartTooltip(t0) {
  const $ = _c(21);
  const {
    active,
    payload,
    label,
    valueLabel,
    detail
  } = t0;
  const value = payload?.[0]?.value;
  if (!active || typeof value !== "number") {
    return null;
  }
  const color = (payload?.[0]?.payload as ChartPoint | undefined)?.color;
  let t1;
  if ($[0] !== detail || $[1] !== value) {
    t1 = detail?.(value);
    $[0] = detail;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const extra = t1;
  let t2;
  if ($[3] !== label) {
    t2 = <p className="mb-1 font-medium text-text-primary">{label}</p>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== color) {
    t3 = <ChartSwatch color={color} />;
    $[5] = color;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== value) {
    t4 = fullNumber.format(value);
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <strong className="ml-auto pl-3 font-semibold tabular-nums text-text-primary">{t4}</strong>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5 || $[13] !== valueLabel) {
    t6 = <p className="flex items-center gap-1.5 text-text-tertiary">{t3}{valueLabel}{t5}</p>;
    $[11] = t3;
    $[12] = t5;
    $[13] = valueLabel;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== extra) {
    t7 = extra && <p className="flex items-center gap-1.5 text-text-tertiary"><ChartSwatch />{extra.label}<strong className="ml-auto pl-3 font-semibold tabular-nums text-text-primary">{extra.value}</strong></p>;
    $[15] = extra;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="min-w-[140px] rounded-popover border border-border-popover bg-bg-surface px-2.5 py-2 text-xs shadow-pop">{t2}{t6}{t7}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
export type ChartProps = {
  /** `line` is an area trend, `column` vertical bars, `bar` horizontal bars sized by row count. */
  kind: "line" | "column" | "bar";
  data: ChartPoint[];
  /** Accessible description of the whole chart. */
  label: string;
  /** Names the value in the tooltip. */
  valueLabel: string;
  /** Plot height for `line` and `column` (196, 220 or 300px); `bar` grows 30px per row. */
  size?: "sm" | "md" | "lg";
  /** Prints each value above (column) or beside (bar) its bar instead of drawing a y axis. */
  values?: boolean;
  /** Mutes every column but the last, for a trend whose latest period matters. */
  highlightLast?: boolean;
  /** A second tooltip line, such as a share of the total. */
  detail?: (value: number) => {
    label: string;
    value: string;
  } | undefined;
  /** Lets a long series scroll sideways instead of squeezing its columns. */
  scroll?: boolean;
};
function scrollWidth(pointCount: number) {
  if (pointCount > 100) return 7200;
  if (pointCount > 50) return 1800;
  if (pointCount > 20) return 1200;
  return 640;
}

/** Use when showing numeric data as a line, column or bar chart. */
export function Chart(t0) {
  const $ = _c(65);
  const {
    kind,
    data,
    label,
    valueLabel,
    size: t1,
    values: t2,
    highlightLast: t3,
    detail,
    scroll: t4
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const values = t2 === undefined ? false : t2;
  const highlightLast = t3 === undefined ? false : t3;
  const scroll = t4 === undefined ? false : t4;
  const t5 = useId();
  let t6;
  if ($[0] !== t5) {
    t6 = t5.replaceAll(":", "");
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const gradient = t6;
  let chart;
  let points;
  if ($[2] !== data || $[3] !== detail || $[4] !== gradient || $[5] !== highlightLast || $[6] !== kind || $[7] !== valueLabel || $[8] !== values) {
    points = data.map(_temp);
    let t7;
    if ($[11] !== detail || $[12] !== valueLabel) {
      t7 = props => <ChartTooltip {...props} valueLabel={valueLabel} detail={detail} />;
      $[11] = detail;
      $[12] = valueLabel;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    const tooltip = t7;
    if (kind === "line") {
      let t8;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          top: 12,
          right: 12,
          bottom: 0,
          left: 0
        };
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      let t10;
      let t9;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <stop offset="0%" stopColor={chartColors.series} stopOpacity={0.12} />;
        t10 = <stop offset="100%" stopColor={chartColors.series} stopOpacity={0} />;
        $[15] = t10;
        $[16] = t9;
      } else {
        t10 = $[15];
        t9 = $[16];
      }
      let t11;
      if ($[17] !== gradient) {
        t11 = <defs><linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">{t9}{t10}</linearGradient></defs>;
        $[17] = gradient;
        $[18] = t11;
      } else {
        t11 = $[18];
      }
      let t12;
      let t13;
      let t14;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = <CartesianGrid vertical={false} stroke="var(--chart-grid-dashed)" strokeDasharray="3 4" />;
        t13 = <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={false} minTickGap={24} tickMargin={10} />;
        t14 = <YAxis tick={tick} tickLine={false} axisLine={false} width={40} tickCount={4} tickFormatter={_temp2} />;
        $[19] = t12;
        $[20] = t13;
        $[21] = t14;
      } else {
        t12 = $[19];
        t13 = $[20];
        t14 = $[21];
      }
      let t15;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {
          stroke: "var(--chart-baseline)",
          strokeWidth: 1
        };
        $[22] = t15;
      } else {
        t15 = $[22];
      }
      let t16;
      if ($[23] !== tooltip) {
        t16 = <Tooltip content={tooltip} cursor={t15} />;
        $[23] = tooltip;
        $[24] = t16;
      } else {
        t16 = $[24];
      }
      const t17 = `url(#${gradient})`;
      let t18;
      if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {
          r: 4,
          strokeWidth: 2,
          stroke: "var(--color-bg-surface)",
          fill: chartColors.series
        };
        $[25] = t18;
      } else {
        t18 = $[25];
      }
      let t19;
      if ($[26] !== t17) {
        t19 = <Area type="monotone" dataKey="value" stroke={chartColors.series} strokeWidth={1.75} fill={t17} dot={false} activeDot={t18} {...still} />;
        $[26] = t17;
        $[27] = t19;
      } else {
        t19 = $[27];
      }
      chart = <AreaChart data={points} margin={t8}>{t11}{t12}{t13}{t14}{t16}{t19}</AreaChart>;
    } else {
      if (kind === "column") {
        const t8 = values ? 20 : 8;
        let t9;
        if ($[28] !== t8) {
          t9 = {
            top: t8,
            right: 4,
            bottom: 0,
            left: 0
          };
          $[28] = t8;
          $[29] = t9;
        } else {
          t9 = $[29];
        }
        let t10;
        if ($[30] !== values) {
          t10 = !values && <CartesianGrid vertical={false} stroke="var(--chart-grid-dashed)" strokeDasharray="3 4" />;
          $[30] = values;
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        let t11;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{
            stroke: "var(--chart-baseline)"
          }} interval="preserveStartEnd" minTickGap={12} tickMargin={8} tickFormatter={categoryLabel} />;
          $[32] = t11;
        } else {
          t11 = $[32];
        }
        let t12;
        if ($[33] !== values) {
          t12 = <YAxis hide={values} tick={tick} tickLine={false} axisLine={false} width={40} tickCount={4} allowDecimals={false} tickFormatter={_temp3} />;
          $[33] = values;
          $[34] = t12;
        } else {
          t12 = $[34];
        }
        let t13;
        if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = {
            fill: "var(--color-fill-hover-subtle)"
          };
          $[35] = t13;
        } else {
          t13 = $[35];
        }
        let t14;
        if ($[36] !== tooltip) {
          t14 = <Tooltip content={tooltip} cursor={t13} />;
          $[36] = tooltip;
          $[37] = t14;
        } else {
          t14 = $[37];
        }
        let t15;
        if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = [3, 3, 0, 0];
          $[38] = t15;
        } else {
          t15 = $[38];
        }
        let t16;
        if ($[39] !== values) {
          t16 = values && <LabelList dataKey="value" position="top" offset={6} formatter={_temp4} className={valueLabelClassName} />;
          $[39] = values;
          $[40] = t16;
        } else {
          t16 = $[40];
        }
        chart = <BarChart data={points} margin={t9} barCategoryGap="24%">{t10}{t11}{t12}{t14}<Bar dataKey="value" radius={t15} maxBarSize={36} {...still}>{points.map((point_0, index) => <Cell key={`${point_0.label}-${index}`} fill={highlightLast && index < points.length - 1 ? chartColors.muted : point_0.color} />)}{t16}</Bar></BarChart>;
      } else {
        const labelWidth = Math.min(180, Math.max(...points.map(_temp5)) * 7 + 12);
        let t8;
        if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = {
            top: 0,
            right: 56,
            bottom: 0,
            left: 0
          };
          $[41] = t8;
        } else {
          t8 = $[41];
        }
        let t9;
        if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <XAxis type="number" hide={true} domain={[0, "dataMax"]} />;
          $[42] = t9;
        } else {
          t9 = $[42];
        }
        let t10;
        if ($[43] !== labelWidth) {
          t10 = <YAxis type="category" dataKey="label" tick={categoryTick} tickLine={false} axisLine={false} width={labelWidth} tickFormatter={_temp6} />;
          $[43] = labelWidth;
          $[44] = t10;
        } else {
          t10 = $[44];
        }
        let t11;
        if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = {
            fill: "var(--color-fill-hover-subtle)"
          };
          $[45] = t11;
        } else {
          t11 = $[45];
        }
        let t12;
        if ($[46] !== tooltip) {
          t12 = <Tooltip content={tooltip} cursor={t11} />;
          $[46] = tooltip;
          $[47] = t12;
        } else {
          t12 = $[47];
        }
        let t13;
        if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
          t13 = <LabelList dataKey="value" position="right" offset={10} formatter={_temp8} className={valueLabelClassName} />;
          $[48] = t13;
        } else {
          t13 = $[48];
        }
        chart = <BarChart data={points} layout="vertical" margin={t8} barCategoryGap={8}>{t9}{t10}{t12}<Bar dataKey="value" radius={3} maxBarSize={14} {...still}>{points.map(_temp7)}{t13}</Bar></BarChart>;
      }
    }
    $[2] = data;
    $[3] = detail;
    $[4] = gradient;
    $[5] = highlightLast;
    $[6] = kind;
    $[7] = valueLabel;
    $[8] = values;
    $[9] = chart;
    $[10] = points;
  } else {
    chart = $[9];
    points = $[10];
  }
  const t7 = kind === "bar" ? "w-full min-w-0" : plotClassName;
  const t8 = kind === "bar" ? points.length * barRowHeight : plotHeights[size];
  let t9;
  if ($[49] !== kind || $[50] !== points || $[51] !== scroll) {
    t9 = scroll && kind === "column" ? scrollWidth(points.length) : 1;
    $[49] = kind;
    $[50] = points;
    $[51] = scroll;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== chart || $[54] !== t8 || $[55] !== t9) {
    t10 = <ResponsiveContainer width="100%" height={t8} minWidth={t9} minHeight={1}>{chart}</ResponsiveContainer>;
    $[53] = chart;
    $[54] = t8;
    $[55] = t9;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== label || $[58] !== t10 || $[59] !== t7) {
    t11 = <div role="img" aria-label={label} className={t7}>{t10}</div>;
    $[57] = label;
    $[58] = t10;
    $[59] = t7;
    $[60] = t11;
  } else {
    t11 = $[60];
  }
  const plot = t11;
  if (!scroll) {
    return plot;
  }
  const t12 = `${label} plot`;
  let t13;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("w-full min-w-0 overflow-x-auto pb-2 outline-none", "focus-visible:outline-1 focus-visible:outline-accent");
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  let t14;
  if ($[62] !== plot || $[63] !== t12) {
    t14 = <div role="region" aria-label={t12} tabIndex={0} className={t13}>{plot}</div>;
    $[62] = plot;
    $[63] = t12;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  return t14;
}
function _temp8(value_2) {
  return typeof value_2 === "number" ? fullNumber.format(value_2) : "";
}
function _temp7(point_2, index_0) {
  return <Cell key={`${point_2.label}-${index_0}`} fill={point_2.color} />;
}
function _temp6(name) {
  return name.length > 26 ? `${name.slice(0, 25)}…` : name;
}
function _temp5(point_1) {
  return point_1.label.length;
}
function _temp4(value_1) {
  return typeof value_1 === "number" ? shortNumber(value_1) : "";
}
function _temp3(value_0) {
  return compactNumber.format(value_0);
}
function _temp2(value) {
  return compactNumber.format(value);
}
function _temp(point) {
  return {
    ...point,
    color: point.color ?? chartColors.series
  };
}